import React, { forwardRef, useContext, useEffect, useImperativeHandle, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { CreateArtifactContext } from "../../../../context/CreateArtifactContext";
import {
  SKArtifact,
  SKDocumentArtifact,
  SKPollsArtifact,
  SKStoryArtifact,
  ContentState,
  getArtefactIcon,
  getContentCreatorActionableItems,
  ContentSubstate,
  TopicTypeString,
  PollType,
} from "../../../../models/SKArtifact";
import { useNavigationBar } from "../../../../context/NavigationContext";

import { capitalizeFirstLetter } from "../../../../utils/stringFunctions";
import { ChoosePublishingSequence } from "./components/ChoosePublishingSequence";
import { ChoosePublishingTier } from "./components/ChoosePublishingTier";
import { AddContentItem } from "./components/AddContentItem";
import { CertifyArtifact } from "./components/CertifyArtifact";
import ArtifactAgeRating from "./components/ArtifactAgeRating";
import ArtifactTopics from "./components/ArtifactTopics";
import { AddTextField } from "../../../../components/AddTextField";
import { AddTextAreaField } from "../../../../components/AddTextAreaField";
import ImageInput from "../ImageInput";
import { AllMediaInput } from "./components/AllMediaInput";
import { ContentStatusView } from "./components/ContentStatusView";
import ContentHistoryNavigator from "./components/ContentHistoryNavigator";
import ContextCheckListCard from "./components/ContextCheckListCard";
import { PublishingTier } from "./components/PublishingTier";
import { ArtifactContainerRef } from "./CreateArtifact";
import ContentEditNavigator from "../../../contentModeration/ContentEditNavigator";
import ContentReportNavigator from "./components/ContentReportNavigator";
import { useAuth, UserRole } from "../../../../context/AuthContext";

const PollsArtifactContainer = forwardRef<ArtifactContainerRef>((props, ref) => {
  // const [isValidForm, setIsValidForm] = useState(false);
  // const [isEdit, setIsEdit] = useState(false);
  const context = useAuth();
  const { contentData, updateContentData, artifactId, isModerator, isEditable, isContentEditing } = useContext(CreateArtifactContext);

  const [error, setErrorState] = useState<{ [key: string]: string | undefined }>({});

  const setErrors = (newErrors: { [key: string]: string | undefined }) => {
    setErrorState(newErrors);
  };

  useImperativeHandle(ref, () => ({
    containerSchema: () => schema,
    setErrors: newErrors => setErrors(newErrors),
  }));

  useEffect(() => {}, []);
  const navigate = useNavigate();
  const { setNavProps } = useNavigationBar();

  useEffect(() => {
    const { pollType } = contentData as SKPollsArtifact;

    if (!pollType) {
      updateContentData({ pollType: PollType.freeTextPoll });
    }
  }, [contentData]);

  const pollOptionSchema = Yup.object({
    text: Yup.string().required("Text is required"),
    count: Yup.number().required("Count is required").min(0, "Count cannot be negative"),
  });

  const pollsOptionsItemsSchema = Yup.object()
    .shape({
      // Use `Yup.object()` with `.shape()` to validate dynamic keys
    })
    .test("is-valid-pollsOptionsItems", "Invalid pollsOptionsItems", value => {
      if (!value || typeof value !== "object") return false;
      return Object.values(value).every(item => pollOptionSchema.isValidSync(item));
    });

  const schema = Yup.object().shape({
    type: Yup.string().required(),
    title: Yup.string().required().max(50),
    subtitle: Yup.string().notRequired().max(500),
    text: Yup.string().notRequired().max(500),
    topic: Yup.string().required(),
    tier: Yup.string().required(),
    age: Yup.number().required(),
    status: Yup.string().required(),
    certifyFirst: Yup.boolean().oneOf([true]).required("Please certify content"),
    certifySecond: Yup.boolean().oneOf([true]).required("Please certify content"),
    // autoPublish: Yup.boolean().required(),
    pollDuration: Yup.string().notRequired(),
    pollQuestion: Yup.string().required("Poll question is required."),
    pollAnswerType: Yup.string().required("Poll answer type is required."),
    pollsOptionsItems: pollsOptionsItemsSchema.required("pollsOptionsItems is required"),
  });

  // useEffect(() => {
  //   setNavProps({
  //     backArrow: true,
  //     title: "Create",
  //     subtitle: capitalizeFirstLetter(contentData.type ?? ""),
  //     icon: contentData.type ? getArtefactIcon(contentData.type) : undefined,
  //     buttonTitle: containerButtonTitle(),
  //     filterOption: false,
  //     enableLink: contentData.status === ContentState.IN_REVIEW ? false : isValidForm,
  //     getActionItems: () => getContentCreatorActionableItems(contentData.status, contentData.subStatus),
  //     onActionItemSelect: actionItem => {},
  //     onNext: submit,
  //     onClose: () => {
  //       navigate(-1);
  //     },
  //   });
  // }, [isValidForm, contentData]);

  // useEffect(() => {
  //   contentData.status !== ContentState.IN_REVIEW ? setIsEdit(false) : setIsEdit(true);
  // }, [contentData]);

  const handleImageChange = (index: number, fileUrl: string | null) => {
    updateContentData({ coverMedia: { ...contentData.coverMedia, coverImage: fileUrl! } });
  };

  return (
    <>
      <div className="artifact-page-context">
        {isModerator &&
        !isContentEditing &&
        artifactId &&
        contentData.status === ContentState.ACTIVE &&
        contentData.subStatus === ContentSubstate.EDIT_IN_REVIEW ? (
          <ContentEditNavigator title="Channel Edit" docId={artifactId!} />
        ) : (
          ""
        )}

        {artifactId && (contentData as SKArtifact).hasHistory ? <ContentHistoryNavigator title="Channel Status" contentId={artifactId} /> : ""}
        {artifactId && context?.userRoles?.includes(UserRole.SkarvoAdmin) && contentData.reportCount && contentData.reportCount > 0 ? (
          <ContentReportNavigator title="Content Reports" />
        ) : (
          ""
        )}
        <AddContentItem
          index={1}
          onClick={() => {
            navigate("addPolls");
          }}
          count={(contentData as SKPollsArtifact)?.pollsOptionsItems ? 1 : 0}
        />

        <AddTextField
          name={"addtitle"}
          title={"Add Title"}
          note={"Title can be the subject, purpose, theme or topic of your post."}
          placeholder={"Enter Title"}
          index={2}
          charAllowed={100}
          value={(contentData as SKArtifact).title}
          onChange={async e => {
            isEditable && updateContentData({ title: e.target.value!, pollType: PollType.freeTextPoll });
          }}
          disabled={!isEditable}
          error={error?.title}
        />

        <AddTextAreaField
          name={"addtext"}
          title={"Add Text"}
          note={"Maximum text length of 500 characters."}
          placeholder={"Enter Text"}
          index={3}
          height={150}
          charAllowed={500}
          value={(contentData as SKArtifact).subtitle as string}
          onChange={async e => {
            isEditable && updateContentData({ subtitle: e.target.value! });
          }}
          disabled={!isEditable}
          error={error?.text}
        />

        <ContextCheckListCard
          index={4}
          name="Add Topic"
          title="Choose the primary topic of your Content."
          purposeText="Add Topic"
          notes="Choose the topic that represents the primary focus of most of your Content."
          doneText={contentData.topic ? capitalizeFirstLetter(TopicTypeString[contentData.topic]) : undefined}
          action={() => {
            navigate("selectTopic");
          }}
          disabled={!isEditable}
        />

        <ContextCheckListCard
          index={5}
          name="Add Age Rating"
          title="Choose the Age Rating of your Content"
          purposeText="Add Age Rating"
          notes="Choose the age rating based on the appropriateness of your Content for children."
          doneText={contentData.age ? "Age " + contentData.age + "+" : undefined}
          action={() => {
            navigate("ageRating");
          }}
          disabled={!isEditable}
        />

        <ContextCheckListCard
          index={6}
          name="Certify Content"
          title="Certify Content"
          purposeText="Certify Content"
          notes="Certify that your Content would ensure and comply with these requirements."
          doneText={contentData.certifyFirst && contentData.certifySecond ? "Certified" : undefined}
          action={() => {
            navigate("certifyContent");
          }}
          disabled={!isEditable}
        />
        <PublishingTier index={7} />
      </div>
    </>
  );
});

export default PollsArtifactContainer;
